import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  Button,
  Surface,
  chipStyle,
  ghostButtonStyle,
  ghostLinkStyle,
  inputStyle,
  pageStyle,
  primaryButtonStyle,
} from '@kilat/ui';
import { Link } from 'react-router-dom';

import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { LOYALTY_CONFIG_DOC_ID, loyaltyConfigRepoFor } from '../lib/repositories/loyalty-repo.js';
import { voucherTemplatesRepoFor } from '../lib/repositories/vouchers-repo.js';
import { logger } from '../lib/logger.js';
import { buildSpinWheelPatch } from '../lib/loyalty-config-patch.js';
import {
  SPIN_WHEEL_MIN_SEGMENTS,
  segmentWinPercents,
  type I18nString,
  type LoyaltyConfig,
  type SpinSegment,
  type VoucherTemplate,
} from '@kilat/shared';

/**
 * /loyalty/spin-wheel — operator-side authoring for the Spin Wheel prize game.
 *
 * The wheel's prize table (segments + odds) + banked-spin cap live INLINE on the
 * single loyalty config doc (`tenants/{tid}/loyaltyProgram/config`.spinWheel), so
 * this is a draft-and-save editor over that doc (like /loyalty/settings), NOT a
 * subcollection CRUD like /loyalty/challenges. Writes go through the
 * rules-guarded `loyaltyConfigRepoFor` (owner/manager) as a FIELD PATCH
 * covering only `spinWheel` + `enabledFeatures.spinWheel`, so editing the wheel
 * cannot disturb earn rules, tiers, punch card, referrals — or the promotions
 * kill switch. (It used to be a read-modify-write of the whole document, which
 * is a lost update by construction on a doc three surfaces write; see G4 and
 * `lib/loyalty-config-patch.ts` for the field-ownership map.)
 *
 * The engine only accrues spins while `enabledFeatures.spinWheel` is on, and the
 * customer can only spin once the wheel is PLAYABLE (>= 2 segments). So the
 * enable toggle is GATED on the segment count (no dead-end: you can't turn on a
 * wheel with nothing to win), and every state is explicit
 * (loading / empty / error / saving / saved) per [[feedback-no-dead-ends]].
 */

type RewardKind = 'none' | 'points' | 'voucher';

interface SegmentDraft {
  key: string;
  id: string;
  label: string;
  originalLabel: I18nString | null;
  rewardKind: RewardKind;
  rewardPoints: string;
  rewardVoucherId: string;
  weight: string;
}

interface WheelDraft {
  enabled: boolean;
  maxBankedSpins: string;
  segments: SegmentDraft[];
}

type SaveStage =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'failed'; message: string };

let segKeySeq = 0;
function newSegKey(): string {
  segKeySeq += 1;
  return `seg-${segKeySeq}`;
}

function newSegmentId(): string {
  return `sw-${Math.random().toString(36).slice(2, 9)}`;
}

function segmentToDraft(s: SpinSegment): SegmentDraft {
  return {
    key: newSegKey(),
    id: s.id,
    label: s.label['en'] ?? '',
    originalLabel: s.label,
    rewardKind: s.reward.kind,
    rewardPoints: s.reward.kind === 'points' ? String(s.reward.amount) : '100',
    rewardVoucherId: s.reward.kind === 'voucher' ? s.reward.voucherTemplateId : '',
    weight: String(s.weight),
  };
}

function configToDraft(c: LoyaltyConfig): WheelDraft {
  const segments = c.spinWheel?.segments ?? [];
  return {
    enabled: c.enabledFeatures.spinWheel === true,
    maxBankedSpins: String(c.spinWheel?.maxBankedSpins ?? 5),
    segments: segments.map(segmentToDraft),
  };
}

export function LoyaltySpinWheelPage() {
  const { claims } = useAuth();
  const t = useI18n();
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;

  const repo = useMemo(() => (tenantId ? loyaltyConfigRepoFor(tenantId) : null), [tenantId]);
  const configSub = useMemo(() => {
    if (!repo) return null;
    return (cb: (v: LoyaltyConfig | null) => void) => repo.snapshot(LOYALTY_CONFIG_DOC_ID, cb);
  }, [repo]);
  const { data: config, loading } = useTrackedSnapshot<LoyaltyConfig | null>(
    ['spin-wheel-config', tenantId],
    configSub,
  );

  const templatesRepo = useMemo(
    () => (tenantId ? voucherTemplatesRepoFor(tenantId) : null),
    [tenantId],
  );
  const templatesSub = useMemo(() => {
    if (!templatesRepo) return null;
    return (cb: (v: VoucherTemplate[]) => void) => templatesRepo.listSnapshot(cb, () => cb([]));
  }, [templatesRepo]);
  const { data: templates } = useTrackedSnapshot<VoucherTemplate[]>(
    ['spin-wheel-voucher-templates', tenantId],
    templatesSub,
  );

  const [draft, setDraft] = useState<WheelDraft | null>(null);
  const [baseline, setBaseline] = useState<WheelDraft | null>(null);
  const [stage, setStage] = useState<SaveStage>({ kind: 'idle' });

  useEffect(() => {
    if (config && !draft) {
      const d = configToDraft(config);
      setDraft(d);
      setBaseline(d);
    }
  }, [config, draft]);

  const isDirty = useMemo(
    () => draft !== null && baseline !== null && JSON.stringify(draft) !== JSON.stringify(baseline),
    [draft, baseline],
  );

  if (!tenantId) return <Screen body={t.t('dashboard.spinWheel.signInPrompt')} />;
  if (loading && !config) return <Screen body={t.t('dashboard.spinWheel.loading')} />;
  if (!config && !loading) {
    return (
      <Screen
        body={t.t('dashboard.spinWheel.errorBody')}
        action={{
          label: t.t('dashboard.spinWheel.reload'),
          onClick: () => window.location.reload(),
        }}
      />
    );
  }
  if (!draft) return <Screen body={t.t('dashboard.spinWheel.loading')} />;

  const weights = draft.segments.map((s) => Math.max(1, Math.round(Number(s.weight) || 0)));
  const percents = segmentWinPercents(weights);
  const hasNoWin = draft.segments.some((s) => s.rewardKind === 'none');
  const enoughSegments = draft.segments.length >= SPIN_WHEEL_MIN_SEGMENTS;

  function patch(p: Partial<WheelDraft>) {
    setDraft((d) => (d ? { ...d, ...p } : d));
    setStage({ kind: 'idle' });
  }

  function updateSegment(key: string, p: Partial<SegmentDraft>) {
    setDraft((d) =>
      d ? { ...d, segments: d.segments.map((s) => (s.key === key ? { ...s, ...p } : s)) } : d,
    );
    setStage({ kind: 'idle' });
  }

  function addSegment() {
    setDraft((d) =>
      d
        ? {
            ...d,
            segments: [
              ...d.segments,
              {
                key: newSegKey(),
                id: newSegmentId(),
                label: '',
                originalLabel: null,
                rewardKind: 'none',
                rewardPoints: '100',
                rewardVoucherId: '',
                weight: '1',
              },
            ],
          }
        : d,
    );
    setStage({ kind: 'idle' });
  }

  function removeSegment(key: string) {
    setDraft((d) => (d ? { ...d, segments: d.segments.filter((s) => s.key !== key) } : d));
    setStage({ kind: 'idle' });
  }

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!draft || !config || !repo) return;

    const maxBanked = Number.parseInt(draft.maxBankedSpins, 10);
    if (!Number.isFinite(maxBanked) || maxBanked < 2 || maxBanked > 20) {
      setStage({ kind: 'failed', message: t.t('dashboard.spinWheel.errMaxBanked') });
      return;
    }

    const built: SpinSegment[] = [];
    for (const s of draft.segments) {
      const label = s.label.trim();
      if (!label) {
        setStage({ kind: 'failed', message: t.t('dashboard.spinWheel.errName') });
        return;
      }
      const weight = Number.parseInt(s.weight, 10);
      if (!Number.isFinite(weight) || weight < 1 || weight > 1000) {
        setStage({ kind: 'failed', message: t.t('dashboard.spinWheel.errWeight') });
        return;
      }
      let reward: SpinSegment['reward'];
      if (s.rewardKind === 'points') {
        const pts = Number.parseInt(s.rewardPoints, 10);
        if (!Number.isFinite(pts) || pts <= 0) {
          setStage({ kind: 'failed', message: t.t('dashboard.spinWheel.errPoints') });
          return;
        }
        reward = { kind: 'points', amount: pts };
      } else if (s.rewardKind === 'voucher') {
        if (!s.rewardVoucherId) {
          setStage({ kind: 'failed', message: t.t('dashboard.spinWheel.errVoucher') });
          return;
        }
        reward = { kind: 'voucher', voucherTemplateId: s.rewardVoucherId };
      } else {
        reward = { kind: 'none' };
      }
      built.push({
        id: s.id,
        label: { ...(s.originalLabel ?? {}), en: label },
        reward,
        weight,
      });
    }

    // Can't turn the wheel ON without something to win (no dead-end): the engine
    // won't let a customer spin a < 2-segment wheel, so block enabling here.
    if (draft.enabled && built.length < SPIN_WHEEL_MIN_SEGMENTS) {
      setStage({ kind: 'failed', message: t.t('dashboard.spinWheel.errMinSegments') });
      return;
    }

    setStage({ kind: 'saving' });
    try {
      await repo.update(
        LOYALTY_CONFIG_DOC_ID,
        buildSpinWheelPatch({ segments: built, maxBankedSpins: maxBanked }, draft.enabled),
      );
      logger.info('spin wheel config saved', {
        tenantId,
        enabled: draft.enabled,
        segments: built.length,
      });
      setBaseline(draft);
      setStage({ kind: 'saved' });
    } catch (err) {
      setStage({
        kind: 'failed',
        message: t.t('dashboard.spinWheel.saveFailed', { error: (err as Error).message ?? '' }),
      });
    }
  }

  return (
    <main style={pageStyle}>
      <header style={headerStyle}>
        <div>
          <h1 style={{ fontSize: 'var(--text-h1)', fontWeight: 600, margin: 0 }}>
            {t.t('dashboard.spinWheel.title')}
          </h1>
          <p style={{ ...metaStyle, marginTop: 'var(--space-1)' }}>
            {t.t(
              draft.segments.length === 1
                ? 'dashboard.spinWheel.countOne'
                : 'dashboard.spinWheel.countOther',
              { count: draft.segments.length },
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Link to="/loyalty/settings" style={ghostLinkStyle}>
            {t.t('dashboard.spinWheel.backToLoyalty')}
          </Link>
          <Link to="/queue" style={ghostLinkStyle}>
            {t.t('dashboard.spinWheel.backToQueue')}
          </Link>
        </div>
      </header>

      <form onSubmit={save} style={{ display: 'grid', gap: 'var(--space-5)' }}>
        <Surface as="section">
          <label style={toggleRowStyle}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontWeight: 600 }}>{t.t('dashboard.spinWheel.enableLabel')}</span>
              <p style={metaStyle}>
                {draft.enabled
                  ? t.t('dashboard.spinWheel.enableOn')
                  : t.t('dashboard.spinWheel.enableOff')}
              </p>
              {!enoughSegments ? (
                <p style={{ ...metaStyle, color: 'var(--color-accent)' }}>
                  {t.t('dashboard.spinWheel.enableNeedsSegments', { n: SPIN_WHEEL_MIN_SEGMENTS })}
                </p>
              ) : null}
            </div>
            <input
              type="checkbox"
              role="switch"
              checked={draft.enabled}
              disabled={!enoughSegments && !draft.enabled}
              onChange={(e) => patch({ enabled: e.target.checked })}
              style={{ accentColor: 'var(--color-primary)', width: 20, height: 20, flexShrink: 0 }}
            />
          </label>

          <label style={{ ...fieldGrid, marginTop: 'var(--space-4)', maxWidth: 260 }}>
            <span style={fieldLabel}>{t.t('dashboard.spinWheel.maxBankedLabel')}</span>
            <input
              inputMode="numeric"
              value={draft.maxBankedSpins}
              onChange={(e) => patch({ maxBankedSpins: e.target.value.replace(/[^\d]/g, '') })}
              style={{ ...inputStyle, fontVariantNumeric: 'tabular-nums' }}
            />
            <span style={hintStyle}>{t.t('dashboard.spinWheel.maxBankedHint')}</span>
          </label>
        </Surface>

        <Surface as="section">
          <div style={sectionHeadRow}>
            <div>
              <h2 style={{ fontSize: 'var(--text-body)', fontWeight: 600, margin: 0 }}>
                {t.t('dashboard.spinWheel.segmentsHeading')}
              </h2>
              <p style={metaStyle}>{t.t('dashboard.spinWheel.segmentsSub')}</p>
            </div>
            <button type="button" onClick={addSegment} style={primaryButtonStyle}>
              {t.t('dashboard.spinWheel.addSegment')}
            </button>
          </div>

          {!hasNoWin && draft.segments.length > 0 ? (
            <p style={{ ...noteStyle, marginTop: 'var(--space-3)' }} role="note">
              {t.t('dashboard.spinWheel.noWinTip')}
            </p>
          ) : null}

          <div style={{ display: 'grid', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
            {draft.segments.length === 0 ? (
              <div style={emptyStyle}>
                <p style={{ ...metaStyle, margin: 0 }}>{t.t('dashboard.spinWheel.empty')}</p>
                <button
                  type="button"
                  onClick={addSegment}
                  style={{ ...primaryButtonStyle, marginTop: 'var(--space-3)' }}
                >
                  {t.t('dashboard.spinWheel.addSegment')}
                </button>
              </div>
            ) : (
              draft.segments.map((seg, i) => (
                <SegmentRow
                  key={seg.key}
                  seg={seg}
                  percent={percents[i] ?? 0}
                  templates={templates ?? []}
                  onChange={(p) => updateSegment(seg.key, p)}
                  onRemove={() => removeSegment(seg.key)}
                />
              ))
            )}
          </div>
        </Surface>

        <div style={saveBarStyle}>
          <button
            type="submit"
            disabled={!isDirty || stage.kind === 'saving'}
            style={{
              ...primaryButtonStyle,
              opacity: !isDirty || stage.kind === 'saving' ? 0.55 : 1,
            }}
          >
            {stage.kind === 'saving'
              ? t.t('dashboard.spinWheel.saving')
              : t.t('dashboard.spinWheel.save')}
          </button>
          {isDirty ? (
            <button
              type="button"
              onClick={() => {
                if (baseline) setDraft(baseline);
                setStage({ kind: 'idle' });
              }}
              style={ghostButtonStyle}
            >
              {t.t('dashboard.spinWheel.discard')}
            </button>
          ) : null}
          {stage.kind === 'saved' && !isDirty ? (
            <span style={{ ...statusStyle, color: 'var(--color-primary)' }}>
              {t.t('dashboard.spinWheel.saved')}
            </span>
          ) : stage.kind === 'failed' ? (
            <span role="alert" style={{ ...statusStyle, color: 'var(--color-accent)' }}>
              {stage.message}
            </span>
          ) : !isDirty ? (
            <span style={statusStyle}>{t.t('dashboard.spinWheel.noChanges')}</span>
          ) : null}
        </div>
      </form>
    </main>
  );
}

function SegmentRow({
  seg,
  percent,
  templates,
  onChange,
  onRemove,
}: {
  readonly seg: SegmentDraft;
  readonly percent: number;
  readonly templates: readonly VoucherTemplate[];
  readonly onChange: (p: Partial<SegmentDraft>) => void;
  readonly onRemove: () => void;
}) {
  const t = useI18n();
  const noTemplates = templates.length === 0;

  return (
    <div style={segmentRowStyle}>
      <div
        style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'flex-end' }}
      >
        <label style={{ ...fieldGrid, flex: '2 1 200px', minWidth: 0 }}>
          <span style={fieldLabel}>{t.t('dashboard.spinWheel.labelLabel')}</span>
          <input
            value={seg.label}
            onChange={(e) => onChange({ label: e.target.value })}
            placeholder={t.t('dashboard.spinWheel.labelPlaceholder')}
            style={inputStyle}
          />
        </label>
        <label style={{ ...fieldGrid, width: 120 }}>
          <span style={fieldLabel}>{t.t('dashboard.spinWheel.weightLabel')}</span>
          <input
            inputMode="numeric"
            value={seg.weight}
            onChange={(e) => onChange({ weight: e.target.value.replace(/[^\d]/g, '') })}
            style={{ ...inputStyle, fontVariantNumeric: 'tabular-nums' }}
          />
        </label>
        <div style={{ ...fieldGrid, width: 96 }}>
          <span style={fieldLabel}>{t.t('dashboard.spinWheel.winChanceLabel')}</span>
          <span style={percentChipStyle}>{percent}%</span>
        </div>
      </div>

      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend style={fieldLabel}>{t.t('dashboard.spinWheel.rewardLegend')}</legend>
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            marginTop: 'var(--space-2)',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            aria-pressed={seg.rewardKind === 'none'}
            onClick={() => onChange({ rewardKind: 'none' })}
            style={chipStyle(seg.rewardKind === 'none')}
          >
            {t.t('dashboard.spinWheel.rewardNone')}
          </button>
          <button
            type="button"
            aria-pressed={seg.rewardKind === 'points'}
            onClick={() => onChange({ rewardKind: 'points' })}
            style={chipStyle(seg.rewardKind === 'points')}
          >
            {t.t('dashboard.spinWheel.rewardPoints')}
          </button>
          <button
            type="button"
            aria-pressed={seg.rewardKind === 'voucher'}
            onClick={() => onChange({ rewardKind: 'voucher' })}
            style={chipStyle(seg.rewardKind === 'voucher')}
          >
            {t.t('dashboard.spinWheel.rewardVoucher')}
          </button>
        </div>

        {seg.rewardKind === 'points' ? (
          <label style={{ ...fieldGrid, marginTop: 'var(--space-3)', maxWidth: 200 }}>
            <span style={fieldLabel}>{t.t('dashboard.spinWheel.pointsLabel')}</span>
            <input
              inputMode="numeric"
              value={seg.rewardPoints}
              onChange={(e) => onChange({ rewardPoints: e.target.value.replace(/[^\d]/g, '') })}
              style={{ ...inputStyle, fontVariantNumeric: 'tabular-nums' }}
            />
          </label>
        ) : null}

        {seg.rewardKind === 'voucher' ? (
          noTemplates ? (
            <div style={{ ...emptyStyle, marginTop: 'var(--space-3)' }}>
              <p style={{ ...metaStyle, margin: 0 }}>{t.t('dashboard.spinWheel.noTemplates')}</p>
              <Link
                to="/loyalty/vouchers"
                style={{
                  ...primaryButtonStyle,
                  marginTop: 'var(--space-3)',
                  textDecoration: 'none',
                  display: 'inline-block',
                }}
              >
                {t.t('dashboard.spinWheel.createVoucher')}
              </Link>
            </div>
          ) : (
            <label style={{ ...fieldGrid, marginTop: 'var(--space-3)', maxWidth: 320 }}>
              <span style={fieldLabel}>{t.t('dashboard.spinWheel.rewardVoucherLabel')}</span>
              <select
                value={seg.rewardVoucherId}
                onChange={(e) => onChange({ rewardVoucherId: e.target.value })}
                style={inputStyle}
              >
                <option value="">{t.t('dashboard.spinWheel.rewardVoucherNone')}</option>
                {templates.map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>
                    {tpl.name[t.locale] ?? tpl.name['en'] ?? tpl.id}
                  </option>
                ))}
              </select>
            </label>
          )
        ) : null}
      </fieldset>

      <Button variant="danger" onClick={onRemove} style={{ justifySelf: 'start' }}>
        {t.t('dashboard.spinWheel.removeSegment')}
      </Button>
    </div>
  );
}

function Screen({
  body,
  action,
}: {
  readonly body: string;
  readonly action?: { label: string; onClick: () => void };
}) {
  return (
    <main className="min-h-dvh flex items-center justify-center p-8 text-center">
      <div style={{ display: 'grid', gap: 'var(--space-3)', justifyItems: 'center' }}>
        <p style={metaStyle}>{body}</p>
        {action ? (
          <button type="button" onClick={action.onClick} style={ghostButtonStyle}>
            {action.label}
          </button>
        ) : null}
      </div>
    </main>
  );
}

// ── styles (mirror /loyalty/challenges + /loyalty/settings) ─────────────────

const headerStyle: React.CSSProperties = {
  marginBottom: 'var(--space-6)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  gap: 'var(--space-4)',
  flexWrap: 'wrap',
};

const sectionHeadRow: React.CSSProperties = {
  display: 'flex',
  gap: 'var(--space-3)',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
};

const segmentRowStyle: React.CSSProperties = {
  display: 'grid',
  gap: 'var(--space-3)',
  padding: 'var(--space-4)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-border)',
  background: 'var(--color-surface)',
};

const fieldLabel: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  fontWeight: 500,
};

const fieldGrid: React.CSSProperties = { display: 'grid', gap: 'var(--space-2)', minWidth: 0 };

const hintStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-on-surface-muted)',
};

const metaStyle: React.CSSProperties = {
  margin: 'var(--space-1) 0 0 0',
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  lineHeight: 1.5,
};

const noteStyle: React.CSSProperties = {
  margin: 0,
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid rgba(234,179,8,0.28)',
  background: 'rgba(234,179,8,0.08)',
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface)',
};

const emptyStyle: React.CSSProperties = {
  padding: 'var(--space-4)',
  borderRadius: 'var(--radius-md)',
  border: '1px dashed var(--color-border-strong)',
  textAlign: 'center',
};

const percentChipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px 12px',
  borderRadius: 'var(--radius-sm)',
  background: 'rgba(13,122,79,0.08)',
  color: 'var(--color-primary)',
  fontWeight: 600,
  fontVariantNumeric: 'tabular-nums',
};

const toggleRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 'var(--space-3)',
  alignItems: 'center',
  padding: 'var(--space-3) var(--space-4)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-border)',
  cursor: 'pointer',
};

const saveBarStyle: React.CSSProperties = {
  display: 'flex',
  gap: 'var(--space-3)',
  alignItems: 'center',
  flexWrap: 'wrap',
};

const statusStyle: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};
